import type {CourseApiModel, CourseExpandedCommon} from '../../../api/course';
import type {CoreCoachCoursesMoveParams} from '../../../api/types/core/methods';
import type {Nullable} from '../../../utils/utility-types';
import type {AsyncStrategy, BaseConfig} from '../utils';

import {courseExpandCommon, courseStore} from '../../../api/course';
import {alert} from '../../../components/alert';
import {isMeCoachForEntity} from '../../../utils/entity/isMeCoachForEntity';
import {AppError} from '../../../utils/errors/app';
import {isDefined} from '../../../utils/typecheck';
import {COMMAND} from '../utils';
import {courseFetch} from './course.fetch';

export {courseUpgrade};

type Config = BaseConfig<
  Omit<CoreCoachCoursesMoveParams, '_id' | 'edition_id'>
> & {
  entity: CourseApiModel;
};

const strategy: AsyncStrategy<Config> = async (roles, config) => {
  if (isMeCoachForEntity(config.entity)) {
    return roles.coach(config);
  } else {
    return roles.client(config);
  }
};

async function courseUpgrade(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

courseUpgrade.asClient = asClient;
courseUpgrade.asCoach = asCoach;

async function asClient(_config: Config) {
  throw new AppError('not_allowed');
}

async function asCoach({entity, params}: Config) {
  if (!(await alert.confirm())) {
    return COMMAND.CANCEL;
  }

  const course = (await courseFetch.asCoach({
    params: {
      _id: entity._id,
      expand: courseExpandCommon,
    },
  })) as CourseExpandedCommon;

  const editionId = course.program.edition_ids!.slice().pop()!;

  const upgradedCourse = (await courseStore.move(entity, {
    ...params,
    edition_id: editionId,
    expand: courseExpandCommon,
  })) as Nullable<CourseExpandedCommon>;

  // If a program is individual, course is deleting after moving to another edition
  if (isDefined(upgradedCourse)) {
    await courseStore.localUpdate(entity, upgradedCourse);
  }

  return upgradedCourse;
}
